import { ConfigProvider } from "antd";
import AppRoutes from "./routes/appRoutes";
import antdCustomTheme from "./theme/antd.config";
import { QueryClientProvider } from "@tanstack/react-query";
import queryConfig from "./api/queryConfig";
import axios from "axios";
import { baseUrl } from "../config";
import { useAuth } from "./store";
import { useEffect } from "react";
import NetworkProtection from "./utils/networkProtection";
import ErrorBoundary from "./components/common/ErrorBoundary";
import NetworkStatus from "./components/common/NetworkStatus";
import {
  setSentryUser,
  clearSentryUser,
  SentryErrorBoundary,
} from "./utils/sentry";

axios.defaults.baseURL = baseUrl + "/api";

function App() {
  const initializeAuth = useAuth((state) => state.initializeAuth);
  const user = useAuth((state) => state.user);
  const isAuthenticated = useAuth((state) => state.isAuthenticated);

  // Initialize authentication and network protection on app start
  useEffect(() => {
    initializeAuth();
    NetworkProtection.initializeProtection();
  }, [initializeAuth]);

  // Set/clear Sentry user context when auth state changes
  useEffect(() => {
    if (isAuthenticated && user) {
      setSentryUser({
        id: user.user_id || user.info?.id?.toString(),
        email: user.info?.email,
        role: user.role,
      });
    } else {
      clearSentryUser();
    }
  }, [isAuthenticated, user]);

  return (
    <SentryErrorBoundary
      fallback={({ error }) => (
        <ErrorBoundary>
          {/* Sentry captures the error, ErrorBoundary shows UI */}
          <div className="hidden">{(error as Error)?.message}</div>
        </ErrorBoundary>
      )}
      showDialog
    >
      <ErrorBoundary>
        <QueryClientProvider client={queryConfig}>
          <ConfigProvider theme={antdCustomTheme} componentSize="large">
            <NetworkStatus />
            <AppRoutes />
          </ConfigProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    </SentryErrorBoundary>
  );
}

export default App;
